import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setMatchedPhotoBackground } from '../../../../../../../../actions/matched-photos';
import useHorizontalSwipe from '../../../../../../../../services/general/useHorizontalSwipe';

const useMatchedPhotoPrintBackgrounds = ({
  matchedPhoto,
  printBackgrounds,
}) => {
  const dispatch = useDispatch();
  const selectedPrintBackgroundId = useSelector(
    ({ matchedPhotos }) => matchedPhotos?.printBackgrounds?.[matchedPhoto.id],
  );
  const setSelectedPrintBackgroundId = useCallback((...args) =>
    dispatch(setMatchedPhotoBackground(...args)),
  );

  useEffect(() => {
    if (
      !printBackgrounds?.find(({ id }) => id === selectedPrintBackgroundId) &&
      printBackgrounds?.length > 0
    ) {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[0].id,
      });
    }
  }, [printBackgrounds]);

  const selectedPrintBackground = useMemo(
    () =>
      printBackgrounds?.find(
        background => background.id === selectedPrintBackgroundId,
      ),
    [printBackgrounds, selectedPrintBackgroundId],
  );

  const onPrevBackground = useCallback(() => {
    if (!printBackgrounds || printBackgrounds?.length === 0) {
      return;
    }

    const currentIndex = printBackgrounds.indexOf(selectedPrintBackground);
    if (currentIndex - 1 < 0) {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[printBackgrounds.length - 1].id,
      });
    } else {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[currentIndex - 1].id,
      });
    }
  }, [selectedPrintBackground, printBackgrounds]);

  const onNextBackground = useCallback(() => {
    if (!printBackgrounds || printBackgrounds?.length === 0) {
      return;
    }

    const currentIndex = printBackgrounds.indexOf(selectedPrintBackground);
    if (currentIndex + 1 === printBackgrounds.length) {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[0].id,
      });
    } else {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[currentIndex + 1].id,
      });
    }
  }, [selectedPrintBackground, printBackgrounds]);

  const swipeHandlers = useHorizontalSwipe({
    onSwipeRight: onPrevBackground,
    onSwipeLeft: onNextBackground,
  });

  return {
    selectedPrintBackground,
    onPrevBackground,
    onNextBackground,
    swipeHandlers,
  };
};

export default useMatchedPhotoPrintBackgrounds;
